import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  forwardRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Calendar, CalendarModule } from 'primeng/calendar';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormsModule,
} from '@angular/forms';
import { formatISO, parseISO } from 'date-fns';
import { SkeletonModule } from 'primeng/skeleton';

@Component({
  selector: 'ls-calendar-atom',
  standalone: true,
  imports: [CommonModule, CalendarModule, FormsModule, SkeletonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalendarAtomComponent),
      multi: true,
    },
  ],
  templateUrl: './calendar-atom.component.html',
  styleUrl: './calendar-atom.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CalendarAtomComponent implements OnInit, ControlValueAccessor {
  value!: string | null; // String ISO
  valueCalendar!: string; 
  date!: any;     // Date ISO
  isDisabled = false;
  onChange = (_: any) => {};
  onTouch = () => {};

  @Input() nextCalendar!: CalendarAtomComponent;
  @Input() loading = false;
  @Input() style!: { [klass: string]: any } | null | undefined;
  @Input() styleClass!: string | undefined;
  @Input() minDate: Date | undefined | null;
  @Input() maxDate!: Date;
  @Input() readonlyInput!: 'true' | 'false';
  @Input() touchUI = false;
  @Input() placeholder!: string;
  @Input() showButtonBar!: boolean;
  @Input() yearNavigator!: boolean;
  @Input() monthNavigator!: boolean;
  @Input() showOtherMonths = false;
  @Input() disabledDates: Date[] = [];
  @Input() required = false;

  @Output() onSelect: EventEmitter<Date> = new EventEmitter();
  @Output() onBlur: EventEmitter<Event> = new EventEmitter();
  @Output() onClose: EventEmitter<any> = new EventEmitter();
  @Output() onInput: EventEmitter<any> = new EventEmitter();
  @Output() onFocus: EventEmitter<Event> = new EventEmitter();
  @Output() onClearClick: EventEmitter<any> = new EventEmitter();

  @ViewChild('calendar', { static: true }) calendar!: Calendar;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit(): void {}

  writeValue(value: string): void {
    this.value = value;
    this.updateModel(value);
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cd.markForCheck();
  }

  handleSelect(value: Date) {
    this.value = formatISO(value, { representation: 'date' });
    this.updateModel(this.value);
    this.onChange(this.value);
    this.onSelect.emit(value);
    if (this.nextCalendar) {
      setTimeout(() => {
        this.nextCalendar.calendar.inputfieldViewChild?.nativeElement.focus();
        this.nextCalendar.calendar.inputfieldViewChild?.nativeElement.click();
      }, 200);
    }
  }

  updateModel(value: string | null) {
    this.date = value ? parseISO(value) : null;
    this.valueCalendar = value ? this.calendar.formatDateTime(this.date) : null;
    this.cd.markForCheck();
  }

  handleBlur(event: Event) {
    this.onBlur.emit(event);
  }
  handleClose(event: any) {
    this.onClose.emit(event);
  }

  handleClearClick(event: any) {
    this.onClearClick.emit(event);
  }

  handleInput(event: InputEvent) {
    this.value = this.calendar.value ? formatISO(this.calendar.value, { representation: 'date' }) : null;
    this.updateModel(this.value);
    this.onChange(this.value);

    this.onInput.emit(event);
  }

  handleFocus(event: Event) {
    if (this.minDate && this.minDate > this.date) {
      this.value = '';
      this.onChange(this.value);
    }
    this.onFocus.emit(event);
  }
}
